import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Phone, MessageSquare, BadgeCheck, Star, MapPin, User } from 'lucide-react';
import { Link } from '@/lib/router-compat';
import { toWhatsAppNumber } from '@/utils/contactInfo';
import { trackContactClick, trackListingMessageClick } from '@/utils/contactTracking';
import { useDealerUrl } from '@/hooks/useDealerUrl';
import { useLang } from '@/i18n/useLang';
import roBuyer from '@/data/translations/ro/buyer.json';
import enBuyer from '@/data/translations/en/buyer.json';
import deBuyer from '@/data/translations/de/buyer.json';

interface Props {
  userId?: string;
  phone?: string;
  email?: string;
  fallbackLocation?: string;
  sellerType?: string | null;
  listingId?: string;
  onReserveClick: () => void;
  onMessageClick: () => void;
}


type Dealer = {
  business_name: string;
  business_address: string | null;
  logo_url: string | null;
  verified: boolean;
  average_rating: number | null;
  total_reviews: number | null;
  years_in_business: number | null;
  business_phone: string | null;
  business_phone_fix: string | null;
  whatsapp_enabled: boolean | null;
};

export const DealerCardV2: React.FC<Props> = ({ userId, phone, email, fallbackLocation, sellerType, listingId, onReserveClick, onMessageClick }) => {
  const isPrivate = sellerType === 'private';
  const dealerUrl = useDealerUrl(isPrivate ? null : userId);
  const lang = useLang();
  const copy = { ro: roBuyer, en: enBuyer, de: deBuyer }[lang];

  const [dealer, setDealer] = useState<Dealer | null>(null);
  const [activeListings, setActiveListings] = useState<number | null>(null);
  const [isSubscribed, setIsSubscribed] = useState<boolean | null>(null);

  useEffect(() => {
    if (!userId) return;
    let canceled = false;
    (async () => {
      const [{ data: dp }, { count }, { data: subData }] = await Promise.all([
        supabase
          .from('dealer_profiles')
          .select('business_name, business_address, logo_url, verified, average_rating, total_reviews, years_in_business, business_phone, business_phone_fix, whatsapp_enabled')
          .eq('user_id', userId)
          .maybeSingle(),
        supabase
          .from('listings_v2')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('status', 'active'),
        supabase.rpc('is_user_subscribed', { _uid: userId }),
      ]);
      if (canceled) return;
      if (dp) setDealer(dp as Dealer);
      setActiveListings(count ?? null);
      setIsSubscribed(subData === true);
    })();
    return () => { canceled = true; };
  }, [userId]);

  // Direct contact (phone/WhatsApp) only for dealers with an active subscription.
  // Private sellers are never gated. Requests via the platform stay open to everyone.
  const directLocked = !isPrivate && ((!!userId && isSubscribed === null) || (!!dealer && isSubscribed === false));
  const contactHidden = false;
  const callPhone = phone || dealer?.business_phone_fix || dealer?.business_phone;
  const mobilePhone = phone || dealer?.business_phone;
  const showWhatsApp = !!mobilePhone && dealer?.whatsapp_enabled !== false;
  const waNumber = toWhatsAppNumber(mobilePhone);

  const address = dealer?.business_address || fallbackLocation;

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden shadow-xs">
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-lg bg-white border border-border flex items-center justify-center overflow-hidden shrink-0 p-1">
            {!isPrivate && dealer?.logo_url ? (
              <img src={dealer.logo_url} alt={dealer.business_name} className="w-full h-full object-contain" />
            ) : isPrivate ? (
              <User className="h-6 w-6 text-primary" />
            ) : (
              <span className="text-primary font-semibold text-lg">{dealer?.business_name?.[0] || 'D'}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-foreground truncate">
              {isPrivate ? (
                'Vânzător privat'
              ) : dealerUrl ? (
                <Link to={dealerUrl} className="hover:text-primary transition-colors">
                  {dealer?.business_name || 'Dealer'}
                </Link>
              ) : (
                dealer?.business_name || 'Vânzător'
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
              {isPrivate && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                  Persoană fizică
                </span>
              )}
              {!isPrivate && dealer?.verified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-900">
                  <BadgeCheck className="h-3 w-3" /> Verificat
                </span>
              )}

              {dealer?.average_rating ? (
                <span className="inline-flex items-center gap-1 text-muted-foreground">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  {Number(dealer.average_rating).toFixed(1)} ({dealer.total_reviews || 0})
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {address && (
          <div className="mt-3 flex items-start gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{address}</span>
          </div>
        )}

        <div className="mt-4 space-y-2">
          {contactHidden ? (
            <div className="rounded-lg border border-dashed border-border bg-muted/40 p-4 text-center text-sm text-muted-foreground">
              Contactul acestui dealer este momentan indisponibil.
            </div>
          ) : (
            <>
              {directLocked && (
                <div className="rounded-lg border border-dashed border-border bg-muted/40 p-3 text-center text-sm text-muted-foreground">
                  Telefonul dealerului nu este disponibil momentan. Trimite o cerere și te contactează el.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
              {!directLocked && callPhone && (
                <Button asChild className="w-full" size="lg">
                  <a href={`tel:${callPhone}`} data-seller-contact onClick={() => listingId && trackContactClick({ id: listingId, sellerId: userId, sellerType }, 'phone', 'seller_card')}>
                    <Phone className="h-4 w-4 mr-2" /> {copy.call}
                  </a>
                </Button>
              )}
              {!directLocked && showWhatsApp && waNumber && (
                <Button asChild variant="outline" className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950" size="lg">
                  <a href={`https://wa.me/${waNumber}`} target="_blank" rel="noopener noreferrer" data-seller-contact onClick={() => listingId && trackContactClick({ id: listingId, sellerId: userId, sellerType }, 'whatsapp', 'seller_card')}>
                    <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.55 4.15 1.6 5.96L2 22l4.25-1.11a9.93 9.93 0 0 0 5.79 1.84h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.91-7.02A9.86 9.86 0 0 0 12.04 2zm5.79 14.13c-.25.7-1.43 1.33-2 1.41-.51.07-1.15.1-1.86-.12-.43-.13-.98-.31-1.69-.62-2.97-1.28-4.91-4.27-5.06-4.47-.15-.2-1.21-1.61-1.21-3.07s.77-2.18 1.04-2.48c.27-.3.59-.37.79-.37l.57.01c.18.01.43-.07.67.51.25.59.84 2.05.91 2.2.07.15.12.32.02.52-.1.2-.15.32-.3.49-.15.17-.31.39-.45.52-.15.15-.3.31-.13.6.17.3.78 1.28 1.67 2.07 1.15 1.02 2.12 1.34 2.42 1.49.3.15.47.13.65-.08.18-.2.74-.86.94-1.16.2-.3.4-.25.67-.15.27.1 1.72.81 2.02.96.3.15.5.22.57.35.07.13.07.74-.18 1.44z"/></svg>
                    WhatsApp
                  </a>
                </Button>
              )}
              </div>
              <Button variant="outline" className="w-full" size="lg" data-seller-contact onClick={() => {
                if (listingId) trackListingMessageClick({ id: listingId, sellerId: userId, sellerType });
                onMessageClick();
              }}>
                <MessageSquare className="h-4 w-4 mr-2" /> {copy.message}
              </Button>
              <Button
                variant="default"
                className="w-full bg-accent text-accent-foreground hover:bg-accent/90"
                size="lg"
                onClick={onReserveClick}
              >
                {isPrivate ? 'Trimite cerere vânzătorului' : 'Trimite cerere către dealer'}
              </Button>
            </>
          )}
        </div>

        {(activeListings !== null || dealer?.years_in_business) && (
          <div className="mt-5 pt-4 border-t border-border grid grid-cols-2 gap-3 text-center">
            {activeListings !== null && (
              <div>
                <div className="text-lg font-semibold text-foreground">{activeListings}</div>
                <div className="text-xs text-muted-foreground">Anunțuri active</div>
              </div>
            )}
            {dealer?.years_in_business && (
              <div>
                <div className="text-lg font-semibold text-foreground">{dealer.years_in_business} ani</div>
                <div className="text-xs text-muted-foreground">Experiență</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
